// src/lib/session/useSession.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { supabaseBrowser } from '@/lib/supabase-browser';

/**
 * useSession — централизованный клиентский доступ к сессии пользователя.
 *
 * Эндпоинты BFF (должны существовать):
 *   GET  /api/session              → 200 { userId, exp, roles[], email?, name? } | 200 { status: 'anon' }
 *   POST /api/session/refresh      → 200 { userId, exp, roles[], email?, name? } | 200 { status: 'anon' }
 *   POST /api/session/signout      → 204
 *
 * Принципы:
 *   - Никаких console.* (0 по всему файлу).
 *   - Zod валидирует формат сессии.
 *   - Гонок нет: запросы отменяются через AbortController.
 *   - Авто-resync при смене видимости вкладки и событии storage (кросс-таб).
 *   - Экспортированы удобные методы: refresh(), signOut().
 */

const SessionSchema = z.object({
  userId: z.string().min(1),
  exp: z.number().int().positive(), // unix seconds
  roles: z.array(z.string()).default([]),
  email: z.string().email().optional(),
  name: z.string().optional(),
});
export type Session = z.infer<typeof SessionSchema> | null;

const isAnonPayload = (payload: unknown): payload is { status?: string } => {
  return typeof payload === 'object' && payload !== null && 'status' in payload;
};

type FetchErrCode = 'NETWORK' | 'TIMEOUT' | 'UNAUTHORIZED' | 'SERVER' | 'BAD_RESPONSE';

function toHumanError(code: FetchErrCode): string {
  switch (code) {
    case 'UNAUTHORIZED': return 'Сессия отсутствует или истекла.';
    case 'TIMEOUT':      return 'Превышено время ожидания ответа.';
    case 'SERVER':       return 'Ошибка сервера.';
    case 'BAD_RESPONSE': return 'Некорректный ответ сервера.';
    case 'NETWORK':
    default:             return 'Сетевая ошибка.';
  }
}

async function withTimeout(input: RequestInfo | URL, init: RequestInit, ms = 7000, signal?: AbortSignal) {
  if (typeof AbortController !== 'undefined') {
    const c = new AbortController();
    const tid = setTimeout(() => c.abort(), ms);
    const composite = new AbortController();
    const onAbort = () => composite.abort();
    c.signal.addEventListener('abort', onAbort);
    signal?.addEventListener('abort', onAbort);
    try {
      const r = await fetch(input, { ...init, signal: composite.signal });
      return r;
    } finally {
      clearTimeout(tid);
      c.signal.removeEventListener('abort', onAbort);
      signal?.removeEventListener('abort', onAbort);
    }
  }
  // фолбэк без AbortController
  return new Promise<Response>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    fetch(input, init)
      .then((r) => { clearTimeout(t); resolve(r); })
      .catch((e) => { clearTimeout(t); reject(e); });
  });
}

async function getSession(signal?: AbortSignal): Promise<Session> {
  try {
    // E2E Testing Bypass
    if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
      const email = window.localStorage.getItem('e2e_user_email') || 'admin@test.com';
      const rolesStr = window.localStorage.getItem('e2e_user_roles') || '["admin"]';
      const roles = JSON.parse(rolesStr);
      return {
        userId: 'test-admin-user-id',
        exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
        roles,
        email,
        name: 'Test Admin'
      };
    }

    // Get JWT token from Supabase client (localStorage)
    const { data: { session: supaSession } } = await supabaseBrowser.auth.getSession();
    const token = supaSession?.access_token;

    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const r = await withTimeout('/api/session', {
      method: 'GET',
      credentials: 'include',
      headers
    }, 7000, signal);

    if (!r.ok) {
      if (r.status === 401) throw new HttpErr('UNAUTHORIZED', r.status);
      if (r.status >= 500) throw new HttpErr('SERVER', r.status);
      throw new HttpErr('BAD_RESPONSE', r.status);
    }
    const json = await r.json().catch(() => { throw new HttpErr('BAD_RESPONSE', 0); });
    if (isAnonPayload(json) && json.status === 'anon') {
      return null;
    }
    const parsed = SessionSchema.safeParse(json);
    if (!parsed.success) {
      throw new HttpErr('BAD_RESPONSE', 0);
    }
    return parsed.data;
  } catch (e) {
    if (e instanceof HttpErr) throw e;
    if (e instanceof Error && (e.name === 'AbortError' || e.message === 'timeout')) {
      throw new HttpErr('TIMEOUT', 0);
    }
    throw new HttpErr('NETWORK', 0);
  }
}

async function refreshSession(signal?: AbortSignal): Promise<Session> {
  try {
    // E2E Testing Bypass
    if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
      const email = window.localStorage.getItem('e2e_user_email') || 'admin@test.com';
      const rolesStr = window.localStorage.getItem('e2e_user_roles') || '["admin"]';
      const roles = JSON.parse(rolesStr);
      return {
        userId: 'test-admin-user-id',
        exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
        roles,
        email,
        name: 'Test Admin'
      };
    }

    // Get refreshed JWT token from Supabase client
    const { data: { session: supaSession } } = await supabaseBrowser.auth.getSession();
    const token = supaSession?.access_token;

    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const r = await withTimeout('/api/session/refresh', {
      method: 'POST',
      credentials: 'include',
      headers,
    }, 7000, signal);

    if (!r.ok) {
      if (r.status === 401) return null; // рефреш не удался — считаем, что сессии нет
      if (r.status >= 500) throw new HttpErr('SERVER', r.status);
      throw new HttpErr('BAD_RESPONSE', r.status);
    }
    const json = await r.json().catch(() => { throw new HttpErr('BAD_RESPONSE', 0); });
    if (isAnonPayload(json) && json.status === 'anon') {
      return null;
    }
    const parsed = SessionSchema.safeParse(json);
    if (!parsed.success) {
      throw new HttpErr('BAD_RESPONSE', 0);
    }
    return parsed.data;
  } catch (e) {
    if (e instanceof HttpErr) throw e;
    if (e instanceof Error && (e.name === 'AbortError' || e.message === 'timeout')) {
      throw new HttpErr('TIMEOUT', 0);
    }
    throw new HttpErr('NETWORK', 0);
  }
}

async function signOutSession(signal?: AbortSignal): Promise<void> {
  const r = await withTimeout('/api/session/signout', {
    method: 'POST', credentials: 'include',
  }, 7000, signal);
  if (r.status !== 204 && r.status !== 200) {
    if (r.status >= 500) throw new HttpErr('SERVER', r.status);
    throw new HttpErr('BAD_RESPONSE', r.status);
  }
}

class HttpErr extends Error {
  constructor(public readonly code: FetchErrCode, public readonly status: number) {
    super(`${code}:${status}`);
    this.name = 'HttpErr';
  }
}

/**
 * Главный хук.
 */
export function useSession(opts?: { /** авто-обновление при видимости вкладки */ resyncOnFocus?: boolean }) {
  const { resyncOnFocus = true } = opts || {};
  const [session, setSession] = useState<Session>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);

  const cancelInFlight = () => { abortRef.current?.abort(); abortRef.current = null; };

  const loadInitial = useCallback(async () => {
    cancelInFlight();
    abortRef.current = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setLoading(true); setError(null);
    try {
      const s = await getSession(abortRef.current?.signal);
      setSession(s);
    } catch (e) {
      const code = e instanceof HttpErr ? e.code : 'NETWORK';
      setError(toHumanError(code));
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    cancelInFlight();
    abortRef.current = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setError(null);
    try {
      const s = await refreshSession(abortRef.current?.signal);
      setSession(s);
    } catch (e) {
      const code = e instanceof HttpErr ? e.code : 'NETWORK';
      setError(toHumanError(code));
      setSession(null);
    }
  }, []);

  const signOut = useCallback(async () => {
    cancelInFlight();
    abortRef.current = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setError(null);
    try {
      await signOutSession(abortRef.current?.signal);
      setSession(null);
      // синхронизация между вкладками
      try { localStorage.setItem('session:event', JSON.stringify({ t: Date.now(), kind: 'signout' })); }
      catch { /* ignore storage errors */ }
    } catch (e) {
      const code = e instanceof HttpErr ? e.code : 'NETWORK';
      setError(toHumanError(code));
    }
  }, []);

  // initial load
  useEffect(() => {
    mountedRef.current = true;
    void loadInitial();
    return () => {
      mountedRef.current = false;
      cancelInFlight();
    };
  }, [loadInitial]);

  // resync on tab focus / visibility change
  useEffect(() => {
    if (!resyncOnFocus) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void loadInitial();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [resyncOnFocus, loadInitial]);

  // cross-tab sync via storage
  useEffect(() => {
    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== 'session:event' || !ev.newValue) return;
      try {
        const payload = JSON.parse(ev.newValue) as { t: number; kind: 'signout'|'refresh' };
        if (payload.kind === 'signout') {
          setSession(null);
        } else if (payload.kind === 'refresh') {
          void loadInitial();
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [loadInitial]);

  const isAuthenticated = useMemo(() => Boolean(session?.userId), [session]);
  const expiresIn = useMemo(() => {
    if (!session?.exp) return null;
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, session.exp - now);
  }, [session?.exp]);

  return {
    session,
    isAuthenticated,
    expiresIn,            // секунды до истечения или null
    loading,
    error,                // человекочитаемый текст
    refresh,
    signOut,
  } as const;
}